import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './store/authStore';
// Corrigido: ajuste o caminho do ColorContext para corresponder ao local correto
import { ColorProvider } from '../src/contexts/ColorContext';
import { CmsProvider } from '../src/contexts/CmsContext';
import { ToastProvider } from './components/ui/Toast';
import Layout from './components/layout/Layout';
import PersonalizedLogin from './pages/PersonalizedLogin';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import OnlineClients from './pages/OnlineClients';
import Resellers from './pages/Resellers';
import CmsUsers from './pages/Users';
import Announcements from './pages/Announcements';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Logs from './pages/Logs';
import Tickets from './pages/Tickets';
import ContentRequests from './pages/ContentRequests';
import VODs from './pages/VODs';
import AppManagements from './pages/AppManagement';
import ShortUrls from './pages/ShortUrls';
import StorePayments from './pages/StorePayments';
import BuyCredits from './pages/BuyCredits';
import Unauthorized from './pages/Unauthorized';
import CmsSelector from './pages/CmsSelector';
import DownloadApp from './pages/DownloadApp';
import DeviceManagement from './pages/DeviceManagement';
import GamesOfDayPreview from './pages/GamesOfDayPreview';
import SecurityManagement from './pages/SecurityManagement';
import ProtectedRoute from './components/ProtectedRoute';
import GlobalSlugRedirect from './components/GlobalSlugRedirect';
import SlugRouteGuard from './components/SlugRouteGuard';
import './styles/dynamic-colors.css';

// Importar utilitários de teste em desenvolvimento
if (process.env.NODE_ENV === 'development') {
  import('./utils/slugRedirectTest');
  import('./utils/testSlugExtraction');
  import('./utils/testApiConnection');
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Simple Protected Route Component (for basic auth check)
const BasicProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuthStore();
  // Não forçar slug padrão, apenas bloquear acesso
  if (!isAuthenticated) {
    return <Unauthorized />;
  }
  return <>{children}</>;
};


function App() {
  const { initializeAuth } = useAuthStore();
  const [isInitializing, setIsInitializing] = React.useState(true);
  
  // Inicializar autenticação baseada no localStorage existente
  React.useEffect(() => {
    const initAuth = async () => {
      try {
        await initializeAuth();
      } catch (error) {
        console.error('❌ Erro ao inicializar autenticação:', error);
        // Limpar dados corrompidos do localStorage
        try {
          const authStorage = localStorage.getItem('auth-storage');
          if (authStorage) {
            try {
              JSON.parse(authStorage);
            } catch {
              // Se não conseguir parsear, limpar
              console.warn('⚠️ Dados de autenticação corrompidos, limpando...');
              localStorage.removeItem('auth-storage');
            }
          }
        } catch (e) {
          console.error('Erro ao limpar localStorage:', e);
        }
      } finally {
        // Sempre definir como false, mesmo em caso de erro
        setIsInitializing(false);
      }
    };
    
    // Timeout de segurança - se demorar mais de 10s, continuar mesmo assim
    let timeoutId: NodeJS.Timeout;
    const timeoutPromise = new Promise<void>((resolve) => {
      timeoutId = setTimeout(() => {
        console.warn('⚠️ Inicialização de autenticação demorou muito, continuando...');
        setIsInitializing(false);
        resolve();
      }, 10000);
    });
    
    // Race entre inicialização e timeout
    Promise.race([
      initAuth(),
      timeoutPromise
    ]).finally(() => {
      clearTimeout(timeoutId);
    });
  }, [initializeAuth]);

  // Mostrar loading durante inicialização
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <CmsProvider>
          <ColorProvider>
            <ToastProvider>
              <div className="App">
              {/* Redirecionamento global para garantir rotas com slug */}
              <GlobalSlugRedirect />
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={<CmsSelector />}
            />
            
            <Route 
              path="/download" 
              element={<DownloadApp />}
            />
            
            <Route 
              path="/:slug/login" 
              element={<PersonalizedLogin />} 
            />
            
            {/* Public route for games of day preview */}
            <Route 
              path="/:slug/games-of-day/preview" 
              element={<GamesOfDayPreview />} 
            />
            
            {/* Protected Routes with slug support */}
            <Route 
              path="/:slug" 
              element={
                <SlugRouteGuard>
                  <BasicProtectedRoute>
                    <Layout />
                  </BasicProtectedRoute>
                </SlugRouteGuard>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route 
                path="users" 
                element={
                  <ProtectedRoute requiredLevel="master">
                    <CmsUsers />
                  </ProtectedRoute>
                } 
              />
              {/* Nota: Sócios têm nível 4 (maior que master 3), então também passam */}
              <Route 
                path="announcements" 
                element={
                  <ProtectedRoute requiredLevel="admin">
                    <Announcements />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="notifications" 
                element={
                  <ProtectedRoute requiredLevel="admin">
                    <Notifications />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="clients" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <Clients />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="online-clients" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <OnlineClients />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="resellers" 
                element={
                  <ProtectedRoute requiredLevel="master">
                    <Resellers />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="p2p-users" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <div>Página de Usuários P2P</div>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="packages" 
                element={
                  <ProtectedRoute requiredLevel="admin">
                    <div>Página de Pacotes</div>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="vods" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <VODs />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="logs" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <Logs />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="settings" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="device-management" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <DeviceManagement />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="tickets" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <Tickets />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="content-requests" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <ContentRequests />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="app-management" 
                element={
                  <ProtectedRoute requiredLevel="admin">
                    <AppManagements />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="short-urls" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <ShortUrls />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="store-payments" 
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <StorePayments />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="buy-credits"
                element={
                  <ProtectedRoute requiredLevel="subreseller">
                    <BuyCredits />
                  </ProtectedRoute>
                }
              />
              <Route 
                path="security-management" 
                element={
                  <ProtectedRoute requiredLevel="admin">
                    <SecurityManagement />
                  </ProtectedRoute>
                } 
              />
            </Route>
            
            {/* Raiz sem slug exibe seletor de CMS */}
            <Route path="/" element={<CmsSelector />} />
            
            {/* Catch-all sem slug: seletor */}
            <Route path="*" element={<CmsSelector />} />
            
            {/* Unauthorized page */}
            <Route path="/unauthorized" element={<Unauthorized />} />
            
            {/* Catch all final */}
            <Route path="*" element={<CmsSelector />} />
          </Routes>
              </div>
            </ToastProvider>
          </ColorProvider>
        </CmsProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;